import { Card, cn } from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import type { LucideIcon } from "lucide-react";
import { ChevronRight } from "lucide-react";
import type { ReactNode } from "react";
import { dashboardLayout } from "../lib/dashboard-layout";

export type DashboardStatCardAccent =
  | "brand"
  | "violet"
  | "emerald"
  | "sky"
  | "amber";

const accentStyles: Record<
  DashboardStatCardAccent,
  { iconWrap: string; icon: string; footerHover: string }
> = {
  brand: {
    iconWrap: "bg-primary/10 ring-1 ring-primary/15",
    icon: "text-primary",
    footerHover: "group-hover:text-primary",
  },
  violet: {
    iconWrap: "bg-violet-500/10 ring-1 ring-violet-500/15",
    icon: "text-violet-600 dark:text-violet-400",
    footerHover: "group-hover:text-violet-600 dark:group-hover:text-violet-400",
  },
  emerald: {
    iconWrap: "bg-emerald-500/10 ring-1 ring-emerald-500/15",
    icon: "text-emerald-600 dark:text-emerald-400",
    footerHover:
      "group-hover:text-emerald-600 dark:group-hover:text-emerald-400",
  },
  sky: {
    iconWrap: "bg-sky-500/10 ring-1 ring-sky-500/15",
    icon: "text-sky-600 dark:text-sky-400",
    footerHover: "group-hover:text-sky-600 dark:group-hover:text-sky-400",
  },
  amber: {
    iconWrap: "bg-amber-500/10 ring-1 ring-amber-500/15",
    icon: "text-amber-600 dark:text-amber-400",
    footerHover: "group-hover:text-amber-600 dark:group-hover:text-amber-400",
  },
};

type DashboardStatCardProps = {
  title: string;
  subtitle?: string;
  icon: LucideIcon;
  accent?: DashboardStatCardAccent;
  to?: string;
  footer?: string;
  /** ຕົວເລກໃຫຍ່ມຸມຂວາ (ເຊັ່ນ %) */
  highlight?: { label: string; value: string };
  children: ReactNode;
};

export function DashboardStatCard({
  title,
  subtitle,
  icon: Icon,
  accent = "brand",
  to,
  footer,
  highlight,
  children,
}: DashboardStatCardProps) {
  const navigate = useNavigate();
  const clickable = Boolean(to);
  const styles = accentStyles[accent];

  return (
    <Card
      className={cn(
        dashboardLayout.cardChrome,
        "group relative flex h-full min-h-[10.5rem] flex-col overflow-hidden border border-border/80 bg-card shadow-sm transition-all",
        clickable &&
          "cursor-pointer hover:border-primary/25 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30",
      )}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={to ? () => navigate({ to }) : undefined}
      onKeyDown={
        to
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                navigate({ to });
              }
            }
          : undefined
      }
    >
      <div className={cn("flex items-start gap-3", dashboardLayout.cardPad)}>
        <div
          className={cn(
            "flex size-11 shrink-0 items-center justify-center rounded-xl",
            styles.iconWrap,
          )}
        >
          <Icon className={cn("size-5", styles.icon)} />
        </div>
        <div className="min-w-0 flex-1 pt-0.5">
          <h3 className="font-semibold text-sm leading-snug tracking-tight">
            {title}
          </h3>
          {subtitle ? (
            <p className="mt-0.5 text-muted-foreground text-xs leading-snug">
              {subtitle}
            </p>
          ) : null}
        </div>
        {highlight ? (
          <div className="shrink-0 text-right">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wide">
              {highlight.label}
            </p>
            <p className="font-bold text-2xl tabular-nums leading-none tracking-tight">
              {highlight.value}
            </p>
          </div>
        ) : null}
      </div>

      <div className={cn("flex flex-1 flex-col", dashboardLayout.statCardBody)}>
        {children}
      </div>

      {footer ? (
        <div
          className={cn(
            "mt-auto flex items-center justify-between gap-2 border-border/60 border-t bg-muted/20",
            dashboardLayout.barPad,
          )}
        >
          <span
            className={cn(
              "font-medium text-muted-foreground text-xs transition-colors",
              styles.footerHover,
            )}
          >
            {footer}
          </span>
          <ChevronRight
            className={cn(
              "size-4 shrink-0 text-muted-foreground/70 transition-transform group-hover:translate-x-0.5",
              styles.icon,
            )}
          />
        </div>
      ) : null}
    </Card>
  );
}
